import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const beaconVariants = cva("rounded-full", {
	variants: {
		variant: {
			neutral: "bg-warm-gray-300",
			running: "bg-blue-300",
			primary: "bg-primary-500",
			secondary: "bg-secondary-500",
			danger: "bg-danger-500",
			success: "bg-success-500",
		},
		size: {
			// Sized for a dot alongside 12px text, where `sm` reads as heavy. Too small to
			// hold a `count`.
			xs: "size-1.5",
			sm: "size-2",
			md: "size-4",
			lg: "size-10",
		},
	},
	defaultVariants: {
		variant: "neutral",
		size: "md",
	},
});

const pingColorMap: Record<
	NonNullable<VariantProps<typeof beaconVariants>["variant"]>,
	string
> = {
	neutral: "bg-warm-gray-200",
	running: "bg-blue-300",
	primary: "bg-primary-300",
	secondary: "bg-secondary-300",
	danger: "bg-danger-300",
	success: "bg-success-300",
};

export type BeaconVariant = NonNullable<
	VariantProps<typeof beaconVariants>["variant"]
>;
export type BeaconSize = NonNullable<
	VariantProps<typeof beaconVariants>["size"]
>;

/**
 * How the beacon draws attention to itself.
 *
 * `"ping"` throws off an expanding ring and leaves the dot still, which reads as an
 * announcement — something has arrived, look here. `"breathe"` swells and fades the dot
 * itself in place, which reads as a condition rather than an event: something is running,
 * and still running. `"none"` is a static dot for a state that is simply true.
 */
export type BeaconAnimation = "ping" | "breathe" | "none";

/** Props for the {@link Beacon} indicator component. Extends `HTMLSpanElement` attributes and beacon variant props. */
export type BeaconProps = React.HTMLAttributes<HTMLSpanElement> &
	VariantProps<typeof beaconVariants> & {
		/** Numeric badge count rendered inside the beacon. */
		count?: number;
		/**
		 * Whether to show the pulse animation. Defaults to `true`.
		 *
		 * Superseded by {@link BeaconProps.animation}, which is more expressive and wins when
		 * both are given. Kept because it is the existing API: `ping={false}` still means
		 * {@link BeaconAnimation | `"none"`}.
		 */
		ping?: boolean;
		/** How the beacon animates. Defaults to `"ping"`, or `"none"` when `ping` is `false`. */
		animation?: BeaconAnimation;
		/**
		 * Overrides the variant's colour with any CSS colour.
		 *
		 * For a beacon whose meaning is not one of the fixed semantic states — a per-agent or
		 * per-tenant tint, say. Prefer a `variant` where one fits, so the palette stays in the
		 * design system.
		 */
		color?: string;
	};

/** Inline so it beats the variant's background class, which stays on for the default case. */
function colorStyle(color: string | undefined) {
	return color ? { backgroundColor: color } : undefined;
}

/** Pulsing status indicator dot. */
const Beacon = React.forwardRef<HTMLSpanElement, BeaconProps>(
	(
		{
			className,
			variant,
			size,
			count,
			ping = true,
			animation,
			color,
			...props
		},
		ref,
	) => {
		const resolvedAnimation = animation ?? (ping ? "ping" : "none");
		// `VariantProps` admits an explicit `null` as well as `undefined`, which a default
		// parameter does not catch and which cannot index the ping colours.
		const resolvedVariant = variant ?? "neutral";

		return (
			<span
				className={cn("relative inline-flex", className)}
				ref={ref}
				{...props}
			>
				{resolvedAnimation === "ping" && (
					// Decorative on its own terms: the dot behind it carries whatever the beacon
					// means, including a `count`, so hiding the ring costs nothing.
					<span
						aria-hidden
						className={cn(
							"animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full opacity-75",
							pingColorMap[resolvedVariant],
						)}
						data-testid="beacon-ring"
						style={colorStyle(color)}
					/>
				)}
				<span
					className={cn(
						"relative inline-flex items-center justify-center rounded-full text-[10px] font-semibold text-white",
						beaconVariants({ variant: resolvedVariant, size }),
						// Applied to the dot rather than a layer behind it, which is the whole
						// difference between breathing and pinging.
						resolvedAnimation === "breathe" &&
							"animate-breathe motion-reduce:animate-none",
					)}
					data-testid="beacon-dot"
					style={colorStyle(color)}
				>
					{count !== undefined ? count : null}
				</span>
			</span>
		);
	},
);
Beacon.displayName = "Beacon";

export { Beacon, beaconVariants };
